import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateScheduledTaskCache, useUpdateScheduledTask } from 'src/api/scheduledTask'
import { invalidateTaskCache } from 'src/api/tasks'
import { AppTextField } from 'src/components/AppTextField'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormTextField } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { IScheduledTask } from 'src/generated-types/scheduledTask'
import { useZodForm } from 'src/hooks/zod-form'
import { getUTCDate } from 'src/utils/function'
import { z } from 'zod'

interface ScheduledTaskEditFormProps {
  initialValues: IScheduledTask
  customerId: EntityId
  pop: (refresh?: boolean) => void
  localityId?: EntityId
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

const ScheduledTaskEditForm: React.FC<ScheduledTaskEditFormProps> = ({
  initialValues,
  customerId,
  pop,
  localityId: localityIdProp,
}) => {
  const { t: translate } = useTranslation(['common', 'task'])
  const localityId = (localityIdProp ?? initialValues?.tasks?.localityId) as EntityId
  const { mutateAsync } = useUpdateScheduledTask(localityId, initialValues?.id as EntityId)

  const workOrderStartDate = initialValues?.tasks?.startDate ? new Date(initialValues.tasks.startDate) : undefined
  const workOrderEndDate = initialValues?.tasks?.endDate ? new Date(initialValues.tasks.endDate) : undefined

  const initialDuration = useMemo(() => {
    if (initialValues?.startDate && initialValues?.endDate) {
      const start = new Date(initialValues.startDate)
      const end = new Date(initialValues.endDate)
      const days = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1
      return days > 0 ? days : 1
    }
    return initialValues?.tasks?.duration ?? 1
  }, [initialValues])

  const schema = z.object({
    startDate: z.date().or(z.string().transform(v => getUTCDate(new Date(v)) || new Date(v))),
    duration: z
      .number()
      .or(z.string().transform(v => parseInt(v)))
      .refine(d => d > 0, { message: translate('durationShouldBePositive', { ns: 'task' }) }),
    description: z.string().max(400).optional().nullable(),
  })

  const form = useZodForm(schema, {
    defaultValues: {
      startDate: initialValues?.startDate,
      duration: initialDuration,
      description: initialValues?.description ?? initialValues?.tasks?.description,
    },
  })

  const onSubmit = useCallback(
    async values => {
      const start = values.startDate instanceof Date ? values.startDate : new Date(values.startDate)
      const endDate = new Date(start)
      endDate.setDate(endDate.getDate() + (Number(values.duration) - 1))

      await mutateAsync(
        { startDate: values.startDate, endDate, description: values.description },
        {
          onSuccess: () => {
            toast.success(translate('updatedSuccessfully', { ns: 'common' }))
            invalidateTaskCache.getCustomerAdminScheduleTaskByFilter(customerId)
            invalidateTaskCache.useGetFilterCalendarScheduleTask(customerId)
            invalidateScheduledTaskCache.useSingleScheduledTask(localityId, initialValues?.id as EntityId)
            invalidateScheduledTaskCache.useScheduledTasks(localityId)
            invalidateScheduledTaskCache.invalidateAllScheduledTasks(localityId)
            pop(true)
          },
          onError: () => {
            toast.error(translate('updatingError', { ns: 'common' }))
          },
        },
      )
    },
    [customerId, initialValues?.id, localityId, mutateAsync, pop, translate],
  )

  return (
    <GenericDialogLayout
      pop={pop}
      removedCloseButton={false}
      actionsDivider
      actions={[
        <MainButton key="update" variant="contained" onClick={() => form.handleSubmit(onSubmit)()}>
          {translate('update', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <AppForm form={form} onSubmit={onSubmit} hasInitialValues={!!initialValues}>
        <AppTextField
          fullWidth
          disabled
          label={translate('title', { ns: 'common' })}
          defaultValue={initialValues?.tasks?.title}
        />
        <BasicDatePicker
          name="startDate"
          label={translate('startDate', { ns: 'common' })}
          minDate={workOrderStartDate}
          maxDate={workOrderEndDate}
        />
        <FormTextField type="number" name="duration" label={translate('duration', { ns: 'task' })} />
        <FormTextField name="description" label={translate('description', { ns: 'common' })} multiline rows={3} />
      </AppForm>
    </GenericDialogLayout>
  )
}

export default ScheduledTaskEditForm
